import React, { useState, useEffect } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import SessionList from './SessionList';
import { sendMessage } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { ReactComponent as MoonIcon } from '../assets/icons/moon.svg';
import { ReactComponent as SunIcon } from '../assets/icons/sun.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as HistoryIcon } from '../assets/icons/history.svg';
import '../styles/Chat.css';

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState(() => {
    const savedSessions = localStorage.getItem('chatSessions');
    return savedSessions ? JSON.parse(savedSessions) : [];
  });
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();

  // 保存会话到 localStorage
  useEffect(() => {
    localStorage.setItem('chatSessions', JSON.stringify(sessions));
  }, [sessions]);

  // 当切换会话时，加载对应的消息
  useEffect(() => {
    if (currentSessionId) {
      const session = sessions.find(s => s.id === currentSessionId);
      if (session) {
        setMessages(session.messages);
      }
    }
  }, [currentSessionId, sessions]);

  const createNewSession = (firstMessage) => {
    const newSession = {
      id: Date.now().toString(),
      title: firstMessage.slice(0, 30) + (firstMessage.length > 30 ? '...' : ''),
      time: new Date().toLocaleString('zh-CN', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      messages: []
    };
    setSessions(prev => [newSession, ...prev]);
    setCurrentSessionId(newSession.id);
    return newSession;
  };

  const handleSendMessage = async (content) => {
    if (!content.trim()) return;
    
    // 如果当前在历史会话页面，切换到聊天页面
    if (showSessions) {
      setShowSessions(false);
    }
    
    let currentSession = sessions.find(s => s.id === currentSessionId);
    if (!currentSession) {
      currentSession = createNewSession(content);
    }

    const userMessage = { 
      role: 'user', 
      content,
      timestamp: new Date().toISOString()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    updateSession(currentSession.id, updatedMessages);
    setLoading(true);

    try {
      const apiMessages = updatedMessages.map(msg => ({ role: msg.role, content: msg.content }));
      
      const assistantMessage = {
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString()
      };
      const newMessages = [...updatedMessages, assistantMessage];
      setMessages(newMessages);
      
      // 立即更新会话，这样在历史记录中就能看到正在生成的会话
      updateSession(currentSession.id, newMessages);

      await sendMessage(apiMessages, (progressMessage) => {
        setMessages(prev => {
          const latest = [...prev];
          latest[latest.length - 1] = {
            ...progressMessage,
            timestamp: assistantMessage.timestamp
          };
          // 实时更新会话内容
          updateSession(currentSession.id, latest);
          return latest;
        });
      });
    } catch (error) {
      console.error('发送消息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSession = (sessionId, newMessages) => {
    setSessions(prev => prev.map(session => {
      if (session.id === sessionId) {
        return {
          ...session,
          messages: newMessages,
          title: newMessages[0]?.content.slice(0, 30) + (newMessages[0]?.content.length > 30 ? '...' : '')
        };
      }
      return session;
    }));
  };

  const handleSelectSession = (sessionId) => {
    setCurrentSessionId(sessionId);
    setShowSessions(false);
    window.scrollTo(0, 0);
  };

  const handleDeleteSession = (sessionId) => {
    if (window.confirm('确定要删除这个会话吗？')) {
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
    }
  };

  const handleNewSession = () => {
    setCurrentSessionId(null);
    setMessages([]);
    if (showSessions) {
      setShowSessions(false);
    }
    // 确保页面滚动到顶部
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 0);
  };

  const handleShowSessions = () => {
    setShowSessions(true);
    // 先重置页面滚动位置
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // 使用 requestAnimationFrame 确保在 DOM 更新后执行滚动
    requestAnimationFrame(() => {
      // 再次确保页面滚动到顶部
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      
      // 等待一小段时间后再滚动会话列表
      setTimeout(() => {
        const sessionList = document.querySelector('.session-list');
        if (sessionList) {
          sessionList.scrollTop = 0;
        }
      }, 100); // 添加一个小延迟确保 DOM 已更新
    });
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="header-left">
          <h1>Kingyo的助手</h1>
        </div>
        <div className="header-right">
          <button 
            className="theme-button" 
            onClick={toggleTheme} 
            title={isDarkMode ? '切换到浅色模式' : '切换到深色模式'}
          >
            {isDarkMode ? <SunIcon className="header-icon" /> : <MoonIcon className="header-icon" />}
          </button>
          <button 
            className="sessions-button"
            onClick={handleShowSessions}
            title="查看历史会话"
          >
            <HistoryIcon className="header-icon" />
          </button>
          <button 
            className="new-chat-button" 
            onClick={handleNewSession} 
            title="开启新会话"
          >
            <PlusIcon className="header-icon" />
          </button>
        </div>
      </div>
      <div className="chat-body">
        {showSessions ? (
          <SessionList 
            sessions={sessions}
            onSelect={handleSelectSession}
            onDelete={handleDeleteSession}
          />
        ) : (
          <>
            <MessageList messages={messages} loading={loading} />
            <MessageInput onSend={handleSendMessage} loading={loading} />
          </>
        )}
      </div>
    </div>
  );
};

export default ChatInterface; 